/**
 * Marketing → SEO (REQ-016).
 *
 * The register and the defect list. Deliberately no composite score: AC-008
 * rules one out, because a single number moves for reasons nobody can act on
 * and a team watching it spends its week on the number rather than the pages.
 *
 * What this shows instead is specific and actionable — which sitemap answers
 * and with how many URLs, which article is past its review interval and by how
 * long, and the two architectural facts that cap what any of this can achieve.
 */
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  XCircle,
} from "lucide-react";
import apiClient from "@/services/api/apiClient";

interface SitemapCheck {
  url: string;
  status: number;
  contentType: string | null;
  urlCount: number;
  isXml: boolean;
}

interface ContentItem {
  slug: string;
  title: string;
  published: boolean;
  lastTouched: string | null;
  ageMonths: number | null;
  reviewIntervalMonths: number;
  needsUpdate: boolean;
  url: string;
}

export default function SeoPage() {
  const sitemaps = useQuery({
    queryKey: ["seo", "sitemaps"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { sitemaps: SitemapCheck[] };
        meta: {
          marketingRoutes: number;
          publishedPosts: number;
          expectedUrls: number;
        };
      }>("/staff/seo/sitemaps");
      return response.data;
    },
  });

  const content = useQuery({
    queryKey: ["seo", "content"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { content: ContentItem[] };
        meta: {
          total: number;
          published: number;
          needingUpdate: number;
          store: string;
        };
      }>("/staff/seo/content");
      return response.data;
    },
  });

  return (
    <div className="space-y-8 p-6">
      <header>
        <h1 className="text-lg font-semibold text-gray-900">SEO</h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-600">
          What is published, how old it is, and whether the things{" "}
          <code>robots.txt</code> promises actually answer. No overall score — a
          composite number moves for reasons nobody can act on.
        </p>
      </header>

      {/* Sitemaps */}
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Sitemaps
        </h2>

        {sitemaps.isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking…
          </div>
        )}

        {sitemaps.data && (
          <div className="space-y-2">
            {sitemaps.data.data.sitemaps.map((sitemap) => {
              // 200 with the SPA's index.html is the trap: healthy to anything
              // that checks the status alone, useless to a crawler.
              const healthy =
                sitemap.status === 200 && sitemap.isXml && sitemap.urlCount > 0;
              return (
                <div
                  key={sitemap.url}
                  className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 text-sm"
                >
                  {healthy ? (
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-600" />
                  ) : (
                    <XCircle className="h-4 w-4 flex-shrink-0 text-red-600" />
                  )}
                  <a
                    href={sitemap.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 font-medium text-sky-700 hover:underline"
                  >
                    {sitemap.url.replace(/^https?:\/\/[^/]+/, "")}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  <span className="text-gray-500">
                    HTTP {sitemap.status || "unreachable"}
                  </span>
                  <span className="text-gray-500">
                    {sitemap.urlCount} URL{sitemap.urlCount === 1 ? "" : "s"}
                  </span>
                  {!sitemap.isXml && sitemap.status === 200 && (
                    <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
                      answers 200 but is not XML
                    </span>
                  )}
                </div>
              );
            })}
            <p className="text-xs text-gray-500">
              {sitemaps.data.meta.marketingRoutes} marketing routes +{" "}
              {sitemaps.data.meta.publishedPosts} published posts ={" "}
              {sitemaps.data.meta.expectedUrls} URLs expected across both.
            </p>
          </div>
        )}
      </section>

      {/* Content register */}
      <section>
        <div className="mb-3 flex flex-wrap items-baseline gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Content
          </h2>
          {content.data && (
            <span className="text-xs text-gray-500">
              {content.data.meta.published} published of{" "}
              {content.data.meta.total}
              {content.data.meta.needingUpdate > 0 && (
                <>
                  {" · "}
                  <span className="font-medium text-amber-700">
                    {content.data.meta.needingUpdate} past review
                  </span>
                </>
              )}
            </span>
          )}
        </div>

        {content.isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {content.isError && (
          <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>The content database did not answer.</span>
          </div>
        )}

        {content.data && content.data.data.content.length === 0 && (
          <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            Nothing published yet.
          </p>
        )}

        {content.data && content.data.data.content.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium">State</th>
                  <th className="px-4 py-3 font-medium">Age</th>
                  <th className="px-4 py-3 font-medium">Review every</th>
                </tr>
              </thead>
              <tbody>
                {content.data.data.content.map((item) => (
                  <tr
                    key={item.slug}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="px-4 py-3">
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-sky-700 hover:underline"
                      >
                        {item.title || item.slug}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      {item.needsUpdate ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          Needs update
                        </span>
                      ) : item.published ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                          Published
                        </span>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-600">
                      {item.ageMonths === null
                        ? "—"
                        : `${item.ageMonths} months`}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-600">
                      {item.reviewIntervalMonths} months
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-2 text-xs text-gray-500">
          Six-month review for anything about vaccination or entry requirements,
          because those change and stale advice about them is the kind that
          hurts somebody. Twelve months otherwise. Nothing is unpublished
          automatically.
        </p>
      </section>

      {/* The two facts that cap everything above */}
      <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <h2 className="text-sm font-semibold text-gray-900">
          Two limits worth knowing before reading anything above as progress
        </h2>
        <ul className="mt-2 space-y-2 text-sm text-gray-600">
          <li>
            <strong className="text-gray-900">
              Public pages are client-rendered.
            </strong>{" "}
            Googlebot executes JavaScript and can read them. Most AI crawlers do
            not — they fetch the HTML and parse what is in it, which for every
            marketing route is an empty <code>&lt;div id="root"&gt;</code> and
            one set of meta tags describing the homepage. Admitting GPTBot in{" "}
            <code>robots.txt</code> while serving it a blank page is not a
            strategy. REQ-016 AC-001.
          </li>
          <li>
            <strong className="text-gray-900">
              Content still lives in the clinical database.
            </strong>{" "}
            Blog posts are rows in PostgreSQL, which{" "}
            <code>DATABASE_ARCHITECTURE.md</code> reserves for health records.
            REQ-016 AC-002 moves them; until it does, this register reads from{" "}
            <code>{content.data?.meta.store ?? "postgres:blog_posts"}</code>.
          </li>
        </ul>
      </section>
    </div>
  );
}
